import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ContactSection from '../components/ContactSection';
import { Building, CheckCircle2, Eye, Ruler, Globe, Users } from 'lucide-react';
import { breadcrumbSchema } from '../utils/structuredData';

const CommercialPropertyToursPage = () => {
  const features = [
    'Photorealistic 3D walkthroughs of offices, retail and industrial spaces',
    'Accurate floor plans with measurement tools',
    'Dollhouse overview and guided tour modes',
    'Custom branding and mattertag annotations',
    'Embed on your website or share via direct link',
    'Integration with property portals and CRM systems'
  ];

  const propertyTypes = [
    {
      title: 'Office Spaces',
      description: 'Give prospective tenants and investors a complete understanding of office layouts, fit-out quality and available space before they set foot on site. 3D tours are particularly valuable for multi-floor buildings where floor-by-floor navigation allows viewers to assess each level independently. Measurement tools let agents and tenants verify dimensions directly within the tour, reducing the need for accompanied viewings that disrupt existing occupiers.'
    },
    {
      title: 'Retail and High Street',
      description: 'Showcase retail units, shopping centres and high street properties with immersive 3D tours that highlight footfall areas, window frontage, storage and back-of-house facilities. National and international retailers can evaluate potential sites remotely, shortlisting only the most suitable units for physical viewing. For landlords, 3D tours demonstrate vacant units to the widest possible audience, accelerating letting timelines.'
    },
    {
      title: 'Industrial and Warehouse',
      description: 'Large industrial spaces are difficult to convey through traditional photography. 3D virtual tours capture the full scale, clear height, loading bay configurations and column spacing of warehouses and industrial units. Prospective occupiers can navigate the space at their own pace, assessing suitability for their operations without arranging a site visit — which can be particularly challenging for occupied or secure premises.'
    }
  ];

  const benefits = [
    {
      icon: Eye,
      title: 'Reduce Unnecessary Viewings',
      description: 'Prospective tenants self-qualify through the virtual tour, meaning only genuinely interested parties request physical viewings.'
    },
    {
      icon: Globe,
      title: 'Reach International Audiences',
      description: 'Overseas investors and tenants can explore properties remotely, expanding your potential market beyond local viewings.'
    },
    {
      icon: Ruler,
      title: 'Built-In Measurement Tools',
      description: 'Viewers can measure any distance within the 3D model, verifying floor areas and spatial arrangements without on-site access.'
    },
    {
      icon: Users,
      title: 'Collaborative Decision-Making',
      description: 'Multiple stakeholders can review the same property independently, making informed decisions without coordinating diaries for group viewings.'
    }
  ];

  const faqs = [
    {
      question: 'How does a commercial property 3D tour differ from residential?',
      answer: 'Commercial property tours are typically larger in scale and require more scan positions to capture the full extent of the space. We also focus on elements that matter to commercial occupiers — ceiling heights, service routes, loading areas, column grids and accessibility. Tours can include mattertag annotations highlighting building specifications, lease terms and fit-out details.'
    },
    {
      question: 'Can I add branding to the virtual tour?',
      answer: 'Yes. We can add your company logo, brand colours, custom splash screens and branded navigation elements to the tour. Mattertag annotations can include your contact details, property specifications and calls-to-action. The tour URL can also be customised for a professional presentation.'
    },
    {
      question: 'How are the tours hosted and shared?',
      answer: 'Tours are hosted on the Matterport platform with 99.9% uptime and global CDN delivery. You receive a unique URL for direct sharing and an embed code for your website. We also offer self-hosted solutions for clients with specific data residency or security requirements.'
    },
    {
      question: 'Can virtual tours integrate with property portals?',
      answer: 'Yes. Matterport virtual tours can be embedded on all major property portals including Rightmove Commercial, CoStar, Zoopla Commercial and specialist platforms. The embed is lightweight and loads quickly without affecting page performance.'
    },
    {
      question: 'How much does a commercial property 3D tour cost?',
      answer: 'Our commercial property scanning starts from £295 for smaller spaces such as individual office suites. Larger multi-floor buildings are quoted individually based on the total area and number of scan positions required. We offer portfolio discounts for agents and landlords with multiple properties. Contact us for a tailored quote.'
    },
    {
      question: 'How long does it take to scan a commercial property?',
      answer: 'A standard commercial office floor can typically be scanned in 2-3 hours. Multi-floor buildings are usually completed within a single day. We schedule scans to minimise disruption to existing occupiers and can work evenings or weekends if required. The completed tour is delivered within 24-48 hours of the site visit.'
    }
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Commercial Property 3D Tours",
    "name": "Commercial Property 3D Tours London",
    "provider": {
      "@type": "Organization",
      "name": "Digital Scanning Group",
      "url": "https://www.digitalscanninggroup.com"
    },
    "areaServed": [
      { "@type": "Place", "name": "London" },
      { "@type": "Place", "name": "United Kingdom" }
    ],
    "description": "Professional 3D virtual tours for commercial properties in London. Offices, retail, industrial and warehouse spaces captured in immersive Matterport 3D."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Commercial Property 3D Tours London | Virtual Property Viewings"
        description="3D virtual tours for commercial properties in London. Offices, retail and industrial spaces. Reduce viewings and reach wider audiences."
        keywords="commercial property virtual tour, 3D office tour london, virtual property viewing, commercial 3D tour, matterport commercial property, virtual office tour"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/commercial-property-3d-tours-london` : ''}
        structuredData={[
          serviceSchema,
          faqSchema,
          breadcrumbSchema([
            { name: 'Home', url: typeof window !== 'undefined' ? window.location.origin : '' },
            { name: 'Commercial Property 3D Tours', url: typeof window !== 'undefined' ? `${window.location.origin}/commercial-property-3d-tours-london` : '' }
          ])
        ]}
      />
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-50 rounded-full mb-6">
              <Building className="h-6 w-6 text-blue-600" />
              <span className="text-blue-600 font-semibold">COMMERCIAL PROPERTY</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Commercial Property 3D Tours London
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase offices, retail units and industrial spaces with immersive 3D virtual tours. Help prospective tenants and investors explore your properties from anywhere, reducing unnecessary viewings and accelerating leasing decisions.
            </p>
            <div className="mt-8">
              <Link
                to="/pricing"
                className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                Get a Quote
              </Link>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-16">
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBzcGFjZXxlbnwwfHx8fA&ixlib=rb-4.1.0&q=85&w=1400"
              alt="Modern Commercial Office Space in London"
              className="w-full h-[500px] object-cover"
            />
          </div>

          {/* Introduction */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                3D Virtual Tours for Commercial Property
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  The commercial property market moves fast. Agents, landlords and developers need to present available space to the widest possible audience with the least possible friction. Traditional methods — arranging accompanied viewings, coordinating diaries, travelling to sites — consume time and resources that could be better spent on negotiation and deal completion.
                </p>
                <p>
                  3D virtual tours transform how commercial property is marketed and viewed. A prospective tenant in another city or country can explore every floor, measure room dimensions, and assess the quality of a fit-out without leaving their desk. By the time they request a physical viewing, they already understand the property and are genuinely interested — dramatically improving conversion rates for agents.
                </p>
                <p>
                  At Digital Scanning Group, we create Matterport 3D tours specifically optimised for commercial property marketing. Our scanning team understands what commercial occupiers look for — clear heights, column grids, floor loading, natural light, accessibility — and captures spaces to highlight these features effectively. We deliver hosted tours with measurement tools, branding options and embed codes ready for your website and property portals.
                </p>
                <p>
                  Whether you are marketing a single office suite or an entire multi-let building, our commercial property tours provide a competitive advantage in a crowded market. Properties with 3D tours consistently attract more enquiries, generate more qualified viewings, and let faster than those relying on photography alone. For a deeper look at the benefits, read our article on <Link to="/blog/virtual-tours-commercial-property" className="text-blue-600 hover:underline">virtual tours for commercial property</Link>.
                </p>
                <p>
                  As an <Link to="/matterport-service-provider-uk" className="text-blue-600 hover:underline">official Matterport Service Provider</Link>, we bring professional equipment and sector expertise to every commercial scanning project. For agents wondering about costs, our guide on <Link to="/blog/how-much-does-matterport-scan-cost-uk" className="text-blue-600 hover:underline">Matterport scan costs in the UK</Link> provides a detailed breakdown. When properties require more than a visual tour — for example, accurate spatial data for fit-out planning — we also offer <Link to="/point-cloud-surveys-london" className="text-blue-600 hover:underline">point cloud surveys</Link> and <Link to="/scan-to-bim-london" className="text-blue-600 hover:underline">Scan to BIM</Link> services from the same site visit.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What You Get</h3>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/pricing"
                className="mt-8 w-full inline-block text-center px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                Get a Quote
              </Link>
            </div>
          </div>

          {/* Property Types */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Property Types We Cover</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              From single office suites to large industrial campuses, we capture commercial spaces of every type and size.
            </p>
            <div className="space-y-6">
              {propertyTypes.map((type, index) => (
                <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{type.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{type.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Benefits for Agents and Landlords</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="p-8 bg-blue-50 rounded-2xl">
                    <div className="flex items-start gap-4">
                      <div className="inline-flex p-4 bg-blue-100 rounded-xl flex-shrink-0">
                        <Icon className="h-8 w-8 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                        <p className="text-gray-600">{benefit.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Related Services */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Related Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link to="/matterport-virtual-tours-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Eye className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Matterport Virtual Tours</h3>
                <p className="text-gray-600 text-sm">Full 3D scanning service for any property type or sector.</p>
              </Link>
              <Link to="/matterport-service-provider-uk" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Globe className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Matterport Service Provider</h3>
                <p className="text-gray-600 text-sm">Official Matterport partner with nationwide coverage.</p>
              </Link>
              <Link to="/point-cloud-surveys-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Ruler className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Point Cloud Surveys</h3>
                <p className="text-gray-600 text-sm">High-accuracy laser scanning for detailed spatial data.</p>
              </Link>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Transform Your Property Marketing?</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Get in touch today for a free consultation and quote. We can scan most commercial properties within a single day.
            </p>
            <Link
              to="/pricing"
              className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default CommercialPropertyToursPage;
